/**
 * @file     shortlistIntent.ts
 * @purpose  Turns an SEO landing page's own context (city, neighbourhood,
 *           building, facet) into saved-search criteria plus the alert events
 *           that page is actually about. Deterministic, no LLM (ADR-009).
 */
import type { SavedSearchCriteria } from '@/services/mls/savedSearchMatch';

/** Alert events the engine already fires on. */
export type ShortlistAlertEvent = 'new_listing' | 'price_drop' | 'open_house';

export interface ShortlistIntent {
  /** What the visitor is subscribing to. */
  criteria: SavedSearchCriteria;
  /** Which events matter for this page family. */
  alertOn: ShortlistAlertEvent[];
  /**
   * Weekly digest for browse-style pages; instant for the event pages, where
   * the whole point is hearing about the event while it is still true.
   */
  frequency: 'weekly' | 'instant';
  /** Saved-search name shown in the dashboard. */
  name: string;
  /** Button copy. Event pages promise the event, not a digest. */
  ctaLabel: string;
  /** One line above the button. */
  blurb: string;
}

const ALL_EVENTS: ShortlistAlertEvent[] = ['new_listing', 'price_drop', 'open_house'];

/**
 * Province-wide hubs (browse by building, browse by feature). The visitor is
 * orienting rather than committed to one place, so the subscription is BC-wide
 * and the ask stays email-only.
 */
export function bcIntent(what: string): ShortlistIntent {
  return {
    criteria: {},
    alertOn: ALL_EVENTS,
    frequency: 'weekly',
    name: `BC ${what}`,
    ctaLabel: 'Get weekly listings',
    blurb: `A weekly email of homes that fit, across BC.`,
  };
}

/**
 * The hot-deals city pages. Their audience is watching price, so the promise is
 * the price drop, which the alert engine does fire on. It deliberately does not
 * promise "tell me when something scores 75+": scoring is not a saved-search
 * criterion, so that would be a promise nothing can keep.
 */
export function dealCityIntent(city: string): ShortlistIntent {
  return {
    criteria: { cities: [city] },
    alertOn: ['price_drop', 'new_listing'],
    frequency: 'instant',
    name: `${city} price watch`,
    ctaLabel: 'Watch prices in ' + city,
    blurb: `Get an email when a ${city} home cuts its price or comes on the market.`,
  };
}

/** A city landing page: market report, off-market, the city hub. */
export function cityIntent(city: string): ShortlistIntent {
  return {
    criteria: { cities: [city] },
    alertOn: ALL_EVENTS,
    frequency: 'weekly',
    name: `${city} homes`,
    ctaLabel: 'Get weekly listings',
    blurb: `A weekly email of homes that fit in ${city}.`,
  };
}

/**
 * A /rentals hub: the subscription is for rentals only (criteria.listingType
 * 'rent', the map's rent-mode value), so the matcher never sends a for-sale home to a renter. `filters`
 * carries the facet's own narrowing (beds, price), never anything about the
 * person (fair housing).
 */
export function rentalIntent(place: string, cities: string[], filters: Pick<SavedSearchCriteria, 'bedsMin'> = {}): ShortlistIntent {
  return {
    criteria: { listingType: 'rent', ...(cities.length ? { cities } : {}), ...filters },
    alertOn: ['new_listing', 'price_drop'],
    frequency: 'instant',
    name: `${place} rentals`,
    ctaLabel: 'Get rental alerts',
    blurb: `An email when a new ${place} rental lists or drops its rent.`,
  };
}

export function neighbourhoodIntent(area: string, city: string | null): ShortlistIntent {
  return {
    criteria: { neighbourhoods: [area], ...(city ? { cities: [city] } : {}) },
    alertOn: ALL_EVENTS,
    frequency: 'weekly',
    name: `${area} homes`,
    ctaLabel: 'Get weekly listings',
    blurb: `A weekly email of homes that fit in ${area}.`,
  };
}

/**
 * Buildings have no axis of their own in SavedSearchCriteria, so a building
 * subscription is its neighbourhood plus the building name as a keyword.
 */
export function buildingIntent(
  building: string,
  area: string | null,
  city: string | null,
): ShortlistIntent {
  return {
    criteria: {
      keywords: building,
      ...(area ? { neighbourhoods: [area] } : {}),
      ...(city ? { cities: [city] } : {}),
    },
    alertOn: ALL_EVENTS,
    frequency: 'weekly',
    name: `${building} units`,
    ctaLabel: 'Tell me when a unit lists',
    blurb: `Get an email when a unit in ${building} is listed.`,
  };
}

/** price-drops / new-listings / open-houses: the page IS the event. */
export function eventIntent(
  event: ShortlistAlertEvent,
  city: string,
): ShortlistIntent {
  const copy: Record<ShortlistAlertEvent, { cta: string; blurb: string; name: string }> = {
    price_drop: {
      cta: `Email me ${city} price drops`,
      blurb: `Get an email when a ${city} seller cuts their price.`,
      name: `${city} price drops`,
    },
    new_listing: {
      cta: `Email me new ${city} listings`,
      blurb: `New ${city} listings, sent as they hit the market.`,
      name: `${city} new listings`,
    },
    open_house: {
      cta: `Email me ${city} open houses`,
      blurb: `This weekend's ${city} open houses.`,
      name: `${city} open houses`,
    },
  };
  const c = copy[event];
  return {
    criteria: { cities: [city] },
    alertOn: [event],
    frequency: 'instant',
    name: c.name,
    ctaLabel: c.cta,
    blurb: c.blurb,
  };
}

/**
 * A listing that can no longer be toured (pending, under contract, coming
 * soon): new listings of the same type in the same place, sent as they list.
 */
export function listingLikeThisIntent({
  area,
  city,
  propertyType,
  noun,
}: {
  area: string | null;
  city: string | null;
  /** Saved-search property type slug, e.g. "condo". */
  propertyType: string | null;
  /** Plural noun for the copy, e.g. "condos". */
  noun: string;
}): ShortlistIntent {
  const place = area ?? city ?? 'BC';
  return {
    criteria: {
      ...(area ? { neighbourhoods: [area] } : {}),
      ...(city ? { cities: [city] } : {}),
      ...(propertyType ? { propertyTypes: [propertyType] } : {}),
    },
    alertOn: ['new_listing'],
    frequency: 'instant',
    name: `${place} ${noun}`,
    ctaLabel: 'Get listing alerts',
    blurb: `New ${place} ${noun} like this one, by email.`,
  };
}
